import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import {
  login as apiLogin,
  logout as apiLogout,
  probeSession,
  registerUnauthorized,
  fetchCsrfToken,
  clearCsrfToken,
  type UserRole,
} from '../api/client'

interface AuthContextValue {
  isAuthenticated: boolean
  isLoading: boolean
  username: string | null
  role: UserRole | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  error: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [username, setUsername] = useState<string | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const clearSession = useCallback(() => {
    setUsername(null)
    setRole(null)
    clearCsrfToken()
  }, [])

  const applySession = useCallback((user: string | null, userRole: UserRole | null) => {
    setUsername(user)
    setRole(userRole)
    if (user) {
      void fetchCsrfToken().catch(() => {
        /* wiki/csrf optional without DB — mutations will retry */
      })
    }
  }, [])

  const refreshSession = useCallback(async () => {
    const probe = await probeSession()
    if (probe.authenticated && probe.username && probe.role) {
      applySession(probe.username, probe.role)
    } else {
      clearSession()
    }
  }, [applySession, clearSession])

  useEffect(() => {
    void (async () => {
      try {
        await refreshSession()
      } finally {
        setIsLoading(false)
      }
    })()
  }, [refreshSession])

  useEffect(() => {
    registerUnauthorized(() => {
      clearSession()
    })
    return () => registerUnauthorized(null)
  }, [clearSession])

  const login = useCallback(async (user: string, pass: string) => {
    setError(null)
    try {
      const resp = await apiLogin(user, pass)
      const probe = await probeSession()
      if (!probe.authenticated || !probe.username) {
        const msg =
          'Signed in, but the browser did not store the session cookie. Stay on the same http/https URL (Edge is HTTP today).'
        setError(msg)
        throw new Error(msg)
      }
      applySession(probe.username, probe.role ?? resp.role)
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.startsWith('Signed in, but')
          ? e.message
          : e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Login failed'
            : 'Login failed'
      setError(msg)
      throw new Error(msg)
    }
  }, [applySession])

  const logout = useCallback(async () => {
    await apiLogout()
    clearSession()
  }, [clearSession])

  const value: AuthContextValue = {
    isAuthenticated: !!username,
    isLoading,
    username,
    role,
    login,
    logout,
    error,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth outside AuthProvider')
  return ctx
}
